import { ChevronDown, PieChart } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Accordion } from '@unistocks/components/accordion/accordion';
import { Image } from '@unistocks/components/basic/image';
import { ConfirmDelete } from '@unistocks/components/delete-button/delete-button';
import { Group } from '@unistocks/components/group/group';
import { UnistocksCircle } from '@unistocks/components/logo/unistocks-circle';
import { Page } from '@unistocks/components/page/page';
import { QueryState } from '@unistocks/components/query-state/query-state';
import {
  PagedResultDtoOfSharedPortfolioDto,
  SharedPortfolioDto
} from '@unistocks/proxy/axios-client';
import {
  sharedPortfoliosGetListQueryKey,
  sharedPortfoliosGetSharedPortfolioQueryKey,
  useSharedPortfoliosDeleteSharedPortfolioUserMutationWithParameters,
  useSharedPortfoliosGetListQuery
} from '@unistocks/proxy/axios-client/SharedPortfoliosQuery';
import { t } from '@unistocks/shared/localization/index.ts';
import { groupBy } from '@unistocks/shared/utils/util';
import { Link } from 'expo-router';
import React from 'react';
import { ListItem, Separator, Square } from 'tamagui';

const mapResult = (result: PagedResultDtoOfSharedPortfolioDto) =>
  Array.from(groupBy(result.items, 'creatorId'));

export const SharedPortfolios: React.FC = () => {
  const queryClient = useQueryClient();
  const {
    data: users,
    isSuccess,
    isRefetching,
    refetch
  } = useSharedPortfoliosGetListQuery(0, 100, {
    select: mapResult
  });

  const { mutate: deleteSharedPortfolio, isPending: isPendingDelete } =
    useSharedPortfoliosDeleteSharedPortfolioUserMutationWithParameters({
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: sharedPortfoliosGetListQueryKey()
        });
        queryClient.removeQueries({
          queryKey: sharedPortfoliosGetSharedPortfolioQueryKey(variables.id)
        });
      }
    });

  return (
    <Page size="small">
      <Page.ScrollView
        headerLogo={false}
        headerTitle={t('::Menu:SharedPortfolios')}
        refreshing={isRefetching}
        onRefresh={refetch}
      >
        <QueryState
          queryKeys={[sharedPortfoliosGetListQueryKey(0, 100)]}
          loadingText={t('::Portfolios:Loading')}
          isEmpty={isSuccess && !users.length}
          noDataIcon={PieChart}
          noDataText={t('::Portfolio:Share:NoSharedPortfolios')}
        >
          {isSuccess && users.length ? (
            <Page.Content p={0}>
              <Accordion type="multiple" defaultValue={users?.length ? [users[0][0]] : undefined}>
                <Group separator={<Separator />}>
                  {users?.map(([userId, sharedPortfolios]: [string, SharedPortfolioDto[]]) => (
                    <Group.Item key={userId}>
                      <Accordion.Item value={userId} overflow="hidden" borderRadius={'$2'}>
                        <Accordion.Trigger>
                          {({ open }: { open: boolean }) => (
                            <ListItem
                              size={'$5'}
                              gap={'$3'}
                              borderRadius={'$2'}
                              title={sharedPortfolios[0].userName}
                              cursor="pointer"
                              icon={
                                <Image
                                  rounded
                                  size={'$2.5'}
                                  source={sharedPortfolios[0].userProfilePictureUri}
                                />
                              }
                              iconAfter={
                                <Square
                                  transition={'quick'}
                                  rotate={open ? '180deg' : '0deg'}
                                  mr={'auto'}
                                >
                                  <ChevronDown size={'$2'} />
                                </Square>
                              }
                            />
                          )}
                        </Accordion.Trigger>
                        <Accordion.Content>
                          <Separator my={'$2'} />
                          <Group separator={<Separator />}>
                            {sharedPortfolios.map(sp => (
                              <Group.Item key={sp.id}>
                                <Link
                                  asChild
                                  withAnchor
                                  href={{
                                    pathname: '/(tabs)/portfolios/shared/[id]',
                                    params: { id: sp.id }
                                  }}
                                >
                                  <ListItem
                                    cursor="pointer"
                                    size={'$5'}
                                    gap={'$3'}
                                    title={sp.portfolioName}
                                    scaleIcon={1.5}
                                    icon={<UnistocksCircle />}
                                    iconAfter={({ size }) => (
                                      <ConfirmDelete
                                        size={size}
                                        message={t('::Portfolio:Share:Delete:Message')}
                                        onConfirm={() => deleteSharedPortfolio({ id: sp.id })}
                                      />
                                    )}
                                    disabled={isPendingDelete}
                                  />
                                </Link>
                              </Group.Item>
                            ))}
                          </Group>
                        </Accordion.Content>
                      </Accordion.Item>
                    </Group.Item>
                  ))}
                </Group>
              </Accordion>
            </Page.Content>
          ) : null}
        </QueryState>
      </Page.ScrollView>
    </Page>
  );
};

SharedPortfolios.displayName = 'SharedPortfolios';
